import { quoteTypes } from './quoteTypes';
import { QuoteType } from './quoteType';
import { ROLES } from '@/services/auth';

/**
 * List of parent companies that only support Medsupp and Health leads and calls.
 */
const medsuppHealthOnlyList = Object.freeze(['AMBETTER', 'USHA']);

/**
 * List of parent companies that do not support Medsupp and Health leads.
 */
const medsuppHealthBlockList = Object.freeze([
  'ALLSTATE',
  // Not a typo. This is how it's named in the database.
  'COUNTRYFINANCIA',
  'FARMERS',
  'STATEFARM',
]);

const lifeBlockList = Object.freeze(['FARMERS', 'STATEFARM']);

/**
 * List of parent companies that are allowed to use medsupp and health.
 * TODO(147374): Remove this when MedicareConsent feature flag is removed.
 */
const medsuppHealthCompanies = Object.freeze([
  'INDEPENDENT',
  'MUTOMA',
  'AMERICANNATIONAL',
  'LIBERTY',
  'NYLIFE',
  'BANKERS',
  'NATIONWIDE',
  'ANCHOR_CLIENT',
  'SIAA',
]);

export default {
  /**
   * Determines the available quote types based on user roles, client type, parent company, and service type.
   *
   * @param {Array<string>} roles - The user's roles
   * @param {string} clientType - The type of client (e.g., 'CORPORATE')
   * @param {string} parentCompany - The client's parent company
   * @param {string} serviceType - The channel service type ('Web' or 'Call')
   * @returns {Array<QuoteType>} An array of available quote types based on the given parameters
   */
  getAvailableQuoteTypes(roles, clientType, parentCompany, serviceType, isMedicareConsentEnabled) {
    if (!isMedicareConsentEnabled) {
      return getAvailableQuoteTypesLegacy(roles, clientType, parentCompany, serviceType);
    }

    const { auto, home, renter, medsupp, health, life } = quoteTypes;
    const isStaffUser = Array.isArray(roles) && roles.includes(ROLES.STAFF);
    const isCorporateClient = clientType === 'CORPORATE';

    // Medsupp/health-only list takes precedence over everything else.
    if (medsuppHealthOnlyList.includes(parentCompany)) {
      return [medsupp, health];
    }

    // Corporate client logic takes precedence over service type.
    if (isCorporateClient) {
      return isStaffUser
        ? [auto, home, renter, medsupp, health, life]
        : [auto, home, renter, medsupp, health];
    }

    if (serviceType === 'Web') {
      const available = [auto, home, renter];

      if (!medsuppHealthBlockList.includes(parentCompany)) {
        available.push(medsupp, health);
      }

      if (!lifeBlockList.includes(parentCompany)) {
        available.push(life);
      }

      return available;
    }

    if (serviceType === 'Call') {
      if (parentCompany === 'COUNTRYFINANCIA') {
        return isStaffUser ? [auto, home] : [];
      }

      return isStaffUser ? [auto, home, life] : [auto, home];
    }

    // Service type not recognized.
    return [];
  },
};

// TODO(147374): Remove this when MedicareConsent feature flag is removed.
function getAvailableQuoteTypesLegacy(roles, clientType, parentCompany, serviceType) {
  const { auto, home, renter, medsupp, health, life } = quoteTypes;
  const isStaffUser = Array.isArray(roles) && roles.includes(ROLES.STAFF);
  const isCorporateClient = clientType === 'CORPORATE';

  // Special case for USHA on Web takes precedence over everything else.
  if (serviceType === 'Web' && parentCompany === 'USHA') {
    return [medsupp, health];
  }

  if (parentCompany === 'AMBETTER') {
    return [medsupp, health];
  }

  // Corporate client logic takes precedence over other cases.
  if (isCorporateClient) {
    return isStaffUser
      ? [auto, home, renter, medsupp, health, life]
      : [auto, home, renter, medsupp, health];
  }

  if (serviceType === 'Web') {
    if (lifeBlockList.includes(parentCompany)) {
      return [auto, home, renter];
    }

    if (medsuppHealthCompanies.includes(parentCompany)) {
      return [auto, home, renter, medsupp, health, life];
    }

    return [auto, home, renter, life];
  }

  if (serviceType === 'Call') {
    if (parentCompany === 'COUNTRYFINANCIA') {
      return isStaffUser ? [auto, home] : [];
    }

    return isStaffUser ? [auto, home, life] : [auto, home];
  }

  // Service type not recognized.
  return [];
}
